import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api, { playersAPI, wyscoutMatchesAPI, AggregateBuildUpAnalysis } from '@/services/api';
import { Player } from '@/types';
import {
  ANALYSIS_TYPES,
  TIME_RANGES,
  AnalysisRequest,
  AnalysisResponse,
} from './AnalysisPage/constants';
import ConfigureAnalysisCard from './AnalysisPage/ConfigureAnalysisCard';
import LoadingOverlay from './AnalysisPage/LoadingOverlay';
import AnalysisResults from './AnalysisPage/AnalysisResults';
import BuildUpAnalysisResults from './AnalysisPage/BuildUpAnalysisResults';
import HelpText from './AnalysisPage/HelpText';

interface AnalysisPageProps {
  embedded?: boolean;
}

export const AnalysisPage: React.FC<AnalysisPageProps> = ({ embedded = false }) => {
  const [selectedAnalysis, setSelectedAnalysis] = useState<string>('');
  const [timeRange, setTimeRange] = useState<string>('month');
  const [userContext, setUserContext] = useState<string>('');
  const [formation, setFormation] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [buildUpAnalysis, setBuildUpAnalysis] = useState<AggregateBuildUpAnalysis | null>(null);

  useEffect(() => {
    const fetchPlayers = async () => {
      setLoadingPlayers(true);
      try {
        const response = await playersAPI.getAll({}, 1, 100); // Get first 100 players
        setPlayers(response.data);
      } catch (error) {
        console.error('Failed to fetch players:', error);
      } finally {
        setLoadingPlayers(false);
      }
    };

    fetchPlayers();
  }, []);

  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayerIds(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const selectAnalysis = (id: string) => {
    setSelectedAnalysis(id);
    setSelectedPlayerIds([]); // Clear player selection when changing analysis type
  };

  const downloadAnalysis = () => {
    if (!analysis || !selectedAnalysisType) return;

    const content = `# ${selectedAnalysisType.name} Analysis Report

**Generated:** ${new Date(analysis.generated_at).toLocaleString()}
**Time Range:** ${TIME_RANGES.find(t => t.id === timeRange)?.name}

## Data Summary
${analysis.data_summary}

## Analysis Results

${analysis.analysis}

---
*Generated by AI Team Analysis Tool*
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedAnalysisType.name.replace(/\s+/g, '_')}_Analysis_${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAnalyze = async () => {
    if (!selectedAnalysis) {
      setError('Please select an analysis type');
      return;
    }

    const selectedAnalysisType = ANALYSIS_TYPES.find(type => type.id === selectedAnalysis);
    if (selectedAnalysisType?.requiresPlayers && selectedPlayerIds.length === 0) {
      setError('Please select at least one player for this analysis type');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis(null);
    setBuildUpAnalysis(null);

    try {
      // Handle build-up analysis separately (no AI endpoint needed)
      if (selectedAnalysis === 'goal_buildup_patterns') {
        const response = await wyscoutMatchesAPI.getAggregateBuildUpAnalysis();
        setBuildUpAnalysis(response.data);
        setLoading(false);
        return;
      }

      const request: AnalysisRequest = {
        analysis_type: selectedAnalysis,
        time_range: timeRange
      };

      if (userContext.trim()) {
        request.user_context = userContext.trim();
      }

      if (formation && selectedAnalysisType?.supportsFormation) {
        request.formation = formation;
      }

      if (selectedAnalysisType?.requiresPlayers && selectedPlayerIds.length > 0) {
        request.player_ids = selectedPlayerIds;
      }

      const response = await api.post('/analysis', request, {
        timeout: 120000 // 2 minutes timeout for AI analysis
      });

      setAnalysis(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };


  const selectedAnalysisType = ANALYSIS_TYPES.find(type => type.id === selectedAnalysis);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center space-x-3">
          <span className="text-3xl">🧠</span>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Team Analysis</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Get AI-powered insights on team performance, wellness, and development
            </p>
          </div>
        </div>
      )}

      {/* Analysis Configuration */}
      <ConfigureAnalysisCard
        selectedAnalysis={selectedAnalysis}
        selectedAnalysisType={selectedAnalysisType}
        timeRange={timeRange}
        formation={formation}
        userContext={userContext}
        loading={loading}
        loadingPlayers={loadingPlayers}
        players={players}
        selectedPlayerIds={selectedPlayerIds}
        onSelectAnalysis={selectAnalysis}
        onTimeRangeChange={setTimeRange}
        onFormationChange={setFormation}
        onUserContextChange={setUserContext}
        onTogglePlayer={togglePlayerSelection}
        onAnalyze={handleAnalyze}
      />

      {/* Error Display */}
      {error && (
        <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <AlertDescription className="text-red-800 dark:text-red-200">{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <LoadingOverlay />
      )}

      {/* Analysis Results */}
      {analysis && (
        <AnalysisResults
          analysis={analysis}
          selectedAnalysisType={selectedAnalysisType}
          onDownload={downloadAnalysis}
        />
      )}

      {/* Build-Up Analysis Results */}
      {buildUpAnalysis && (
        <BuildUpAnalysisResults analysis={buildUpAnalysis} />
      )}

      {/* Help Text */}
      <HelpText />
    </div>
  );
};
